import { useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, KeyRound, Loader2, Plus, Store, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useShopeeConfig,
  useSaveShopeeConfig,
  useConnectShopee,
  useShopeeAccounts,
  useDeleteZaloAccount,
  type ShopeeConfig,
  type ShopeeEnv,
} from '@/hooks/use-integrations'
import {
  SelectableTable,
  accountColumns,
  accountSearchText,
  ACCOUNT_FILTERS,
  BulkDisconnectButton,
} from './accounts-table'
import { CopyBox } from './channel-setup-guide'
import { noConnectHint, useAutoAdd } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { OAUTH_RETURN_TO } from '@/lib/onboarding'

const ENV_LABEL: Record<ShopeeEnv, string> = {
  live: 'Live — partner.shopeemobile.com',
  test: 'Test (Sandbox V2) — openplatform.sandbox.test-stable.shopee.sg',
}

/** Domain của Redirect URL — khai ở "Test/Live Redirect URL Domain" trong Console. */
function redirectDomainOf(uri: string): string {
  try {
    return new URL(uri).origin
  } catch {
    return uri
  }
}

/**
 * Nhắc khai Redirect URL Domain. Link uỷ quyền mới (open.shopee.com/auth) bắt redirect_uri
 * thuộc domain đã khai — thiếu là Shopee chặn ngay trên trang uỷ quyền, không quay về eCDP.
 */
function RedirectDomainNotice({ cfg }: { cfg: ShopeeConfig }) {
  const { t } = useTranslation('chanSocial')
  return (
    <div className="space-y-2 rounded-lg border bg-muted/40 p-3 text-sm">
      <p>
        <Trans
          t={t}
          i18nKey="shopee.redirect.notice"
          values={{ env: cfg.env === 'test' ? 'Test' : 'Live' }}
          components={{ b: <strong />, em: <em /> }}
        />
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <CopyBox label={t('shopee.redirect.domainLabel')} value={redirectDomainOf(cfg.redirectUri)} />
        <CopyBox label="Push URL (Live/Test Push Callback)" value={cfg.pushUrl} />
      </div>
      <p className="text-xs text-muted-foreground">
        {t('shopee.redirect.pushHint')}
      </p>
    </div>
  )
}

/**
 * Cảnh báo quyền chat. Trích docs/KET-NOI-UU-TIEN-TIKTOK-SHOPEE-TELEGRAM.md §3.2 —
 * người bán cần biết TRƯỚC khi tốn công tạo app rằng kết nối xong chưa chắc có chat.
 */
function WhitelistNotice() {
  const { t } = useTranslation('chanSocial')
  return (
    <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="space-y-1.5">
        <p className="font-medium">{t('shopee.whitelist.title')}</p>
        <p className="text-muted-foreground">
          <Trans t={t} i18nKey="shopee.whitelist.p1" components={{ b: <strong /> }} />
        </p>
        <p className="text-muted-foreground">
          <Trans t={t} i18nKey="shopee.whitelist.p2" components={{ b: <strong /> }} />
        </p>
      </div>
    </div>
  )
}

/** Form Partner ID / Partner Key / môi trường (lưu theo org, khoá mã hoá phía máy chủ). */
function CredentialsForm({ cfg }: { cfg: ShopeeConfig }) {
  const { t } = useTranslation('chanSocial')
  const [partnerId, setPartnerId] = useState(cfg.partnerId)
  const [partnerKey, setPartnerKey] = useState('')
  const [pushPartnerKey, setPushPartnerKey] = useState('')
  const [env, setEnv] = useState<ShopeeEnv>(cfg.env)
  const save = useSaveShopeeConfig()

  const handleSave = () => {
    if (!/^\d+$/.test(partnerId.trim())) {
      toast.error(t('shopee.cred.partnerIdInvalid'))
      return
    }
    if (!cfg.hasPartnerKey && !partnerKey.trim()) {
      toast.error(t('shopee.cred.partnerKeyRequired'))
      return
    }
    save.mutate(
      {
        partnerId: partnerId.trim(),
        partnerKey: partnerKey.trim() || undefined,
        pushPartnerKey: pushPartnerKey.trim() || undefined,
        env,
      },
      {
        onSuccess: (r) => {
          toast.success(r.message || t('shopee.cred.saved'))
          setPartnerKey('')
          setPushPartnerKey('')
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          {cfg.isConfigured ? t('shopee.cred.titleUpdate') : t('shopee.cred.titleSetup')}
          {cfg.isConfigured && (
            <Badge variant="success" className="ml-1">
              {t('shared.configured')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <Trans
            t={t}
            i18nKey="shopee.cred.intro"
            components={{
              b: <strong />,
              link: (
                <a
                  href="https://open.shopee.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary underline"
                />
              ),
            }}
          />
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Partner ID</label>
            <Input
              value={partnerId}
              inputMode="numeric"
              onChange={(e) => setPartnerId(e.target.value)}
              placeholder={t('shopee.cred.partnerIdPlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('shopee.cred.env')}</label>
            <Select value={env} onValueChange={(v) => setEnv(v as ShopeeEnv)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">{ENV_LABEL.live}</SelectItem>
                <SelectItem value="test">{ENV_LABEL.test}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Partner Key</label>
          <Input
            type="password"
            value={partnerKey}
            onChange={(e) => setPartnerKey(e.target.value)}
            placeholder={
              cfg.hasPartnerKey ? t('shopee.cred.keySaved', { masked: cfg.partnerKeyMasked }) : t('shopee.cred.keyPaste')
            }
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Push Partner Key (Live)</label>
          <Input
            type="password"
            value={pushPartnerKey}
            onChange={(e) => setPushPartnerKey(e.target.value)}
            placeholder={
              cfg.hasPushPartnerKey
                ? t('shopee.cred.keySaved', { masked: cfg.pushPartnerKeyMasked })
                : t('shopee.cred.pushKeyPaste')
            }
          />
          <p className="text-xs text-muted-foreground">
            {t('shopee.cred.pushKeyHint')}
          </p>
        </div>

        <RedirectDomainNotice cfg={cfg} />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />}
            {t('shared.saveConfig')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/** Danh sách shop Shopee đã uỷ quyền. */
function ConnectedShops() {
  const { t } = useTranslation('chanSocial')
  const { data, isLoading, isError } = useShopeeAccounts()
  const remove = useDeleteZaloAccount()
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canDelete = !!perms?.has('integrations.delete')

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success(t('shopee.shops.disconnected')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <SelectableTable
      rows={data}
      rowKey={(a) => a.id}
      loading={isLoading}
      error={isError}
      filters={ACCOUNT_FILTERS}
      searchText={accountSearchText}
      itemLabel={t('shopee.shops.itemLabel')}
      empty={
        <EmptyState
          icon={Store}
          title={t('shopee.shops.emptyTitle')}
          description={
            canCreate ? t('shopee.shops.emptyDescription') : noConnectHint()
          }
        />
      }
      columns={accountColumns({
        fallbackName: 'Shopee',
        sub: (acc) => (acc.externalPageId ? `Shop ID ${acc.externalPageId}` : undefined),
        actions: canDelete
          ? (acc) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDisconnect(acc.id)}
                disabled={remove.isPending}
                title={t('common:actions.disconnect')}
              >
                <Unplug />
              </Button>
            )
          : undefined,
      })}
      bulkActions={(selected, clear) => <BulkDisconnectButton selected={selected} onDone={clear} />}
    />
  )
}

/**
 * Tab Shopee — cấu hình app (Partner ID/Key, Live/Test), uỷ quyền shop qua
 * link `open.shopee.com/auth`, quản lý shop đã kết nối. Chat chưa bật (chờ whitelist).
 */
export function ShopeeTab() {
  const { t } = useTranslation('chanSocial')
  const { data: cfg, isLoading, isError } = useShopeeConfig()
  const connect = useConnectShopee()
  // Kết nối shop = POST /shopee/connect/start (create); lưu khoá ứng dụng = POST /shopee/config (update).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')

  // Mở từ trang Bắt đầu → callback OAuth đưa về /onboarding (returnTo lưu trong state phía server).
  const onboardingReturn = useOnboardingReturn()
  const handleConnect = () => {
    connect.mutate(onboardingReturn.active ? { returnTo: OAUTH_RETURN_TO } : undefined, {
      onSuccess: (url) => {
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  useAutoAdd(handleConnect, cfg?.isConfigured === true)

  if (isLoading) return <Loading label={t('shopee.loading')} />
  if (isError || !cfg) return <ErrorState />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('shopee.intro')}
          {cfg.env === 'test' && <Badge variant="warning" className="ml-2">{t('shopee.testEnvBadge')}</Badge>}
        </p>
        {canCreate && (
          <Button
            onClick={handleConnect}
            disabled={connect.isPending || !cfg.isConfigured}
            title={cfg.isConfigured ? '' : t('shopee.configFirst')}
          >
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            {t('shopee.connect')}
          </Button>
        )}
      </div>

      <WhitelistNotice />

      {canUpdate && <CredentialsForm cfg={cfg} />}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('shopee.shops.heading')}</h3>
        <ConnectedShops />
      </section>
    </div>
  )
}
